import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Drawer from '../../Fundamentals/Drawer/Drawer';
import FormField from '../../Fundamentals/Form/FormField';
import EnumSelect from '../../Fundamentals/Form/EnumSelect';
import DatePicker, { DEFAULT_DATE_FORMAT } from '../../Fundamentals/DatePicker';
import dateUtils from '../../../Utils/dateUtils';
import { allowedTransitions, packageStatusConfig } from './packageConstants';
import {
    SCHEDULED_CHANGE_NOTE_MAX_LENGTH,
    emptyScheduledChangeDraft,
    isSchedulableDate,
    isValidScheduledChangeDraft,
    scheduledChangeDraftErrors,
    toScheduledChangeDraft,
    toScheduledChangeRequest,
} from './scheduledChangeUtils';
import type { VossVersionStatus } from '../../../Constants/VOSS/vossVersionStatus';
import type { PackageScheduledChange, PackageScheduledChangeRequest, ScheduledChangeDraft } from './Packages.types';

interface PackageScheduledChangeDrawerProps {
    // undefined schedules a new change for the version
    change?: PackageScheduledChange;
    currentStatus: VossVersionStatus | undefined;
    isSaving: boolean;
    onClose: () => void;
    onSave: (model: PackageScheduledChangeRequest) => void;
}

const PackageScheduledChangeDrawer = ({ change, currentStatus, isSaving, onClose, onSave }: PackageScheduledChangeDrawerProps) => {
    const { t } = useTranslation();
    const targets = allowedTransitions(currentStatus);
    const [draft, setDraft] = useState<ScheduledChangeDraft>(() => (change === undefined ? emptyScheduledChangeDraft(targets) : toScheduledChangeDraft(change)));

    const isNew = change === undefined;
    const today = dateUtils.formatDate(new Date());
    const originalDate = change?.scheduledDate === undefined ? undefined : dateUtils.formatDate(change.scheduledDate);
    const errors = scheduledChangeDraftErrors(draft, today, originalDate);
    const canSave = isValidScheduledChangeDraft(draft, today, originalDate) && !isSaving;
    const update = <K extends keyof ScheduledChangeDraft>(key: K, value: ScheduledChangeDraft[K]) => setDraft((prev) => ({ ...prev, [key]: value }));

    // the target is fixed once scheduled (VSCO-3791 edits name, date and note); keep it visible even if the version has moved on since
    const statusOptions = isNew ? targets : draft.targetStatus === null ? [] : [draft.targetStatus];
    const scheduledBy = change?.reference ?? '';
    const subtitle = isNew ? t('packages.scheduleChangeSubtitle') : scheduledBy.length > 0 ? t('packages.scheduledChangeBy', { user: scheduledBy }) : undefined;

    return (
        <Drawer
            title={(isNew ? t('packages.scheduleChange') : t('packages.editScheduledChange')) ?? ''}
            subtitle={subtitle ?? undefined}
            onClose={onClose}
            canClose={!isSaving}
            footer={
                <>
                    <button type="button" className="btn btn-default" disabled={isSaving} onClick={onClose}>
                        {t('common.cancel')}
                    </button>
                    <button type="button" className="btn btn-primary" disabled={!canSave} onClick={() => onSave(toScheduledChangeRequest(draft))}>
                        {isSaving && <span className="spinner spinner-sm mr-8" />}
                        {isNew ? t('packages.scheduleChange') : t('common.save')}
                    </button>
                </>
            }
        >
            <div className="alert alert-info mb-16">{t('packages.scheduledChangeInfo')}</div>

            <FormField label={t('packages.targetStatus') ?? ''} htmlFor="scheduled-change-status" required help={t('packages.targetStatusHelp') ?? ''}>
                <EnumSelect
                    id="scheduled-change-status"
                    disabled={!isNew}
                    value={draft.targetStatus}
                    options={statusOptions}
                    getLabel={(status) => t(packageStatusConfig[status].labelKey)}
                    onChange={(targetStatus) => update('targetStatus', targetStatus)}
                />
            </FormField>

            <DatePicker
                label={t('packages.scheduledDate')}
                required
                showTime={false}
                placeholderText="YYYY-MM-DD"
                value={draft.scheduledDate.length === 0 ? null : draft.scheduledDate}
                isValidDate={(date) => isSchedulableDate(date.format(DEFAULT_DATE_FORMAT), today, originalDate)}
                error={errors.scheduledDate && draft.scheduledDate.length > 0 ? t('packages.scheduledDateInvalid') : undefined}
                onChange={(value, dateObj) => update('scheduledDate', dateObj === null ? '' : value)}
            />

            <FormField label={t('packages.newPackageName') ?? ''} htmlFor="scheduled-change-name" help={t('packages.newPackageNameHelp') ?? ''}>
                <input id="scheduled-change-name" type="text" className="form-control" value={draft.name} onChange={(e) => update('name', e.target.value)} />
            </FormField>

            <FormField
                label={t('packages.note') ?? ''}
                htmlFor="scheduled-change-note"
                help={`${t('packages.noteHelp') ?? ''} (${draft.note.length}/${SCHEDULED_CHANGE_NOTE_MAX_LENGTH})`}
                error={errors.note ? (t('packages.noteTooLong', { max: SCHEDULED_CHANGE_NOTE_MAX_LENGTH }) ?? '') : undefined}
            >
                <textarea id="scheduled-change-note" className="form-control" rows={3} value={draft.note} onChange={(e) => update('note', e.target.value)} />
            </FormField>
        </Drawer>
    );
};

export default PackageScheduledChangeDrawer;
